import React, { useState } from 'react';
import { t } from '@lingui/macro';

import CarouselLeftSmall from '@rover/icons/svg/carousel-left-small.svg';
import { Box, Button, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import DialogModal from '@rover/react-lib/src/components/modals/DialogModal';

type Props = {
  href: string;
  headerText?: string;
  modalText?: string;
  cancelButtonText?: string;
  submitButtonText?: string;
  skipModal?: boolean;
  onToggleOpen?: (isOpen: boolean) => void;
};

/**
 * Back button with unsaved changes warning modal.
 * Non-Redux version that uses skipModal to control modal behavior.
 * Use this for modern forms that don't rely on Redux for state management.
 * Set skipModal={false} to show the modal, skipModal={true} to skip it.
 */
export const BackButtonWithFormState = ({
  href,
  headerText = t`Unsaved changes`,
  modalText = t`Any unsaved changes will be lost. Are you sure you want to leave this page?`,
  cancelButtonText = t`Cancel`,
  submitButtonText = t`Leave page`,
  skipModal = true,
  onToggleOpen,
}: Props): JSX.Element => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const toggleIsOpen = (): void => {
    const nextIsOpen = !isOpen;
    setIsOpen(nextIsOpen);

    if (onToggleOpen) {
      onToggleOpen(nextIsOpen);
    }
  };

  const navigateAway = (): void => window.location.assign(href);

  const handleBackClick = (): void => {
    if (!skipModal) {
      toggleIsOpen();
    } else {
      navigateAway();
    }
  };

  return (
    <Box>
      <Box textAlign="left">
        <Button
          icon={CarouselLeftSmall}
          variant="flat"
          onPress={handleBackClick}
          size="small"
          pl="0x"
          sx={{
            '&:hover,&:focus': {
              color: theme.colors.interactive_link.primary_pressed,
              backgroundColor: theme.background.interactive.primary_base,
            },
            svg: {
              width: '24px',
              height: '24px',
            },
          }}
        >
          <Text size="100" fontWeight="regular">
            {t`Back`}
          </Text>
        </Button>
      </Box>
      <DialogModal
        headerText={headerText}
        isOpen={isOpen}
        onRequestClose={toggleIsOpen}
        cancelButtonText={cancelButtonText}
        onClickCancel={toggleIsOpen}
        submitButtonText={submitButtonText}
        onClickSubmit={navigateAway}
      >
        {modalText}
      </DialogModal>
    </Box>
  );
};

export default BackButtonWithFormState;
